import { Nullable } from '../../../../types';
import {
  ControlledSelectGroup,
  ControlledSelectOption,
  ControlledSelectValue,
} from '../types/ControlledSelectOption';

type ControlledSelectSelectedOption<IsMulti extends boolean> =
  IsMulti extends true
    ? ControlledSelectOption[]
    : Nullable<ControlledSelectOption>;

const flattenOptions = (
  options: ControlledSelectOption[] | undefined,
  groups: ControlledSelectGroup[] | undefined,
): ControlledSelectOption[] => {
  if (groups) {
    return groups.flatMap((g) => g.options);
  }
  return options ?? [];
};

const findOptionFromOptions = (
  options: ControlledSelectOption[] | undefined,
  value: Nullable<ControlledSelectOption['value']>,
): Nullable<ControlledSelectOption> =>
  options?.find((opt) => String(opt.value) === String(value)) ?? null;

const findSingleSelectedOption = (
  options: ControlledSelectOption[] | undefined,
  value: Nullable<ControlledSelectValue<false>>,
): Nullable<ControlledSelectOption> => findOptionFromOptions(options, value);

const findMultiSelectedOptions = (
  options: ControlledSelectOption[] | undefined,
  value: ControlledSelectValue<true>,
): ControlledSelectOption[] =>
  value
    .map((selectedValue) => findOptionFromOptions(options, selectedValue))
    .filter((opt): opt is ControlledSelectOption => opt !== null);

export function findSelectedOption(
  options: ControlledSelectOption[] | undefined,
  groups: ControlledSelectGroup[] | undefined,
  value: Nullable<ControlledSelectValue<false>>,
  isMulti?: false,
): Nullable<ControlledSelectOption>;
export function findSelectedOption(
  options: ControlledSelectOption[] | undefined,
  groups: ControlledSelectGroup[] | undefined,
  value: Nullable<ControlledSelectValue<true>>,
  isMulti: true,
): ControlledSelectOption[];
export function findSelectedOption<IsMulti extends boolean>(
  options: ControlledSelectOption[] | undefined,
  groups: ControlledSelectGroup[] | undefined,
  value: Nullable<ControlledSelectValue<IsMulti>>,
  isMulti: IsMulti,
): ControlledSelectSelectedOption<IsMulti>;

export function findSelectedOption(
  options: ControlledSelectOption[] | undefined,
  groups: ControlledSelectGroup[] | undefined,
  value: Nullable<ControlledSelectValue<true> | ControlledSelectValue<false>>,
  isMulti = false,
): ControlledSelectSelectedOption<boolean> {
  const allOptions = flattenOptions(options, groups);

  if (isMulti) {
    return findMultiSelectedOptions(
      allOptions,
      Array.isArray(value) ? value : [],
    );
  }

  return findSingleSelectedOption(
    allOptions,
    Array.isArray(value) ? null : value,
  );
}
